import * as React from 'react';
import { Search, Lock, Users2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { FolderType } from '@/types';
import { useRole } from '@/features/settings/hooks';
import type { RootState } from '@/reducers';
import type { AppDispatch } from '@/store';
import { updatePermissions, fetchFolders } from '../../librarySlice';

interface FolderAccessModalProps {
  open: boolean;
  onClose: () => void;
  folder: FolderType;
}

// Access Type Card Component
interface AccessTypeCardProps {
  isSelected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
}

function AccessTypeCard({
  isSelected,
  onClick,
  icon,
  title,
  description,
  disabled,
}: AccessTypeCardProps) {
  return (
    <Card
      className={cn(
        'cursor-pointer transition-all',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'hover:border-muted-foreground/50',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
      onClick={disabled ? undefined : onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
          <div
            className={cn(
              'p-2 rounded-lg',
              isSelected ? 'bg-primary/10 text-primary' : 'bg-muted',
            )}
          >
            {icon}
          </div>
          <div className="flex-1 space-y-1">
            <h4
              className={cn(
                'text-sm font-medium',
                isSelected && 'text-primary',
              )}
            >
              {title}
            </h4>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Member Status Badge Component
interface MemberStatusBadgeProps {
  userId: string;
  folderId?: string;
  teamOwnerId?: string;
  isOwner: boolean;
  hasAccess: boolean;
}

function MemberStatusBadge({
  userId,
  folderId,
  teamOwnerId,
  isOwner,
  hasAccess,
}: MemberStatusBadgeProps) {
  const { t } = useTranslation();

  if (userId === folderId || isOwner) {
    return (
      <Badge variant="default">
        {t('library.folder.permissions.owner', 'Owner')}
      </Badge>
    );
  }

  if (userId === teamOwnerId) {
    return (
      <Badge variant="outline">
        {t('library.folder.access.teamOwner', 'Team Owner')}
      </Badge>
    );
  }

  if (hasAccess) {
    return (
      <Badge variant="secondary">
        {t('library.folder.permissions.hasAccess', 'Has access')}
      </Badge>
    );
  }

  return (
    <Badge variant="outline">
      {t('library.folder.permissions.noAccess', 'No access')}
    </Badge>
  );
}

// Main Component
export default function FolderAccessModal({
  open,
  onClose,
  folder,
}: FolderAccessModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { isRole } = useRole();

  const { teamMembers, selectedTeam, currentUser, teamId } = useSelector(
    (state: RootState) => ({
      teamMembers: state.team.selectedTeam?.teamSpaceMembers || [],
      selectedTeam: state.team.selectedTeam,
      currentUser: state.settings.user,
      teamId: state.team.selectedTeam?.teamId,
    }),
  );

  // Check if user can manage permissions
  const canManagePermissions = React.useMemo(() => {
    const currentUserId = currentUser?._id;
    return (
      isRole(['Owner', 'Admin']) ||
      currentUserId === folder.ownerId ||
      currentUserId === selectedTeam?.ownerId
    );
  }, [currentUser?._id, folder.ownerId, selectedTeam?.ownerId, isRole]);

  // State management
  const [isPublic, setIsPublic] = React.useState<boolean>(
    folder.permissions === null,
  );
  const [selectedUsers, setSelectedUsers] = React.useState<string[]>(() => {
    const ownerId = folder.ownerId || '';
    const permissionUsers = (folder.permissions || [])
      .map((p) => p.userId)
      .filter((id): id is string => typeof id === 'string' && id !== ownerId);
    return ownerId ? [ownerId, ...permissionUsers] : permissionUsers;
  });
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [hasChanges, setHasChanges] = React.useState(false);

  // Get owner info
  const owner = React.useMemo(() => {
    return (
      teamMembers.find((member) => member._id === selectedTeam?.ownerId) || {
        _id: selectedTeam?.ownerId || '',
        email: selectedTeam?.email || '',
        name: t('library.folder.access.teamOwner', 'Team Owner'),
        picture: null,
        status: true,
        role: 'Owner',
        createdAt: '',
        inviteAccepted: true,
        lastActiveAt: '',
        updatedAt: '',
      }
    );
  }, [teamMembers, selectedTeam, t]);

  // Combine members
  const allMembers = React.useMemo(() => {
    return teamMembers.some((member) => member._id === owner._id)
      ? teamMembers
      : [owner, ...teamMembers];
  }, [teamMembers, owner]);

  // Filter members based on search
  const filteredMembers = React.useMemo(() => {
    return allMembers.filter((member) => {
      const searchTerm = searchQuery.toLowerCase();
      return (
        member.name?.toLowerCase().includes(searchTerm) ||
        member.email.toLowerCase().includes(searchTerm)
      );
    });
  }, [allMembers, searchQuery]);

  // Track changes
  React.useEffect(() => {
    const originalIsPublic = folder.permissions === null;
    const originalUsers = folder.permissions
      ? [folder.ownerId, ...folder.permissions.map((p) => p.userId)].filter(
          Boolean,
        )
      : [folder.ownerId].filter(Boolean);

    const currentUsers = selectedUsers.slice().sort();
    const originalUsersSorted = originalUsers.slice().sort();

    const changed =
      originalIsPublic !== isPublic ||
      JSON.stringify(currentUsers) !== JSON.stringify(originalUsersSorted);

    setHasChanges(changed);
  }, [isPublic, selectedUsers, folder]);

  const handleToggleUser = React.useCallback(
    (userId: string, checked: boolean) => {
      if (userId === folder.ownerId) return; // Can't toggle owner

      setSelectedUsers((prev) =>
        checked ? [...prev, userId] : prev.filter((id) => id !== userId),
      );
    },
    [folder.ownerId],
  );

  const handleAccessTypeChange = React.useCallback(
    (newIsPublic: boolean) => {
      setIsPublic(newIsPublic);
      if (newIsPublic) {
        // When switching to public, include all team members
        setSelectedUsers(folder.ownerId ? [folder.ownerId] : []);
      } else if (folder.ownerId && !selectedUsers.includes(folder.ownerId)) {
        // When switching to restricted, keep current selection
        setSelectedUsers([folder.ownerId, ...selectedUsers]);
      }
    },
    [folder.ownerId, selectedUsers],
  );

  const handleSave = React.useCallback(async () => {
    if (isSaving || !hasChanges) return;

    try {
      setIsSaving(true);
      setError(null);

      await dispatch(
        updatePermissions({
          folderId: folder._id,
          permissions: isPublic
            ? null
            : selectedUsers.map((userId) => ({ userId })),
        }),
      );

      if (teamId) {
        await dispatch(fetchFolders({ teamId }));
      }

      onClose();
    } catch (err) {
      // Handle specific known errors
      if (
        err instanceof TypeError &&
        err.message.includes('Cannot read properties of undefined')
      ) {
        if (teamId) {
          await dispatch(fetchFolders({ teamId }));
        }
        onClose();
        return;
      }

      console.error('Failed to update permissions:', err);
      setError(
        t(
          'library.folder.permissions.error',
          'Failed to update permissions. Please try again.',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    dispatch,
    folder._id,
    selectedUsers,
    isPublic,
    onClose,
    teamId,
    t,
    isSaving,
    hasChanges,
  ]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t('library.folder.access.title', 'Folder Access')} - {folder.name}
          </DialogTitle>
          <DialogDescription>
            {canManagePermissions
              ? t(
                  'library.folder.access.manageDescription',
                  'View and manage who has access to this folder',
                )
              : t(
                  'library.folder.access.viewDescription',
                  'View who has access to this folder',
                )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Access Type Selection - Only show if user can manage */}
          {canManagePermissions && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <AccessTypeCard
                  isSelected={isPublic}
                  onClick={() => handleAccessTypeChange(true)}
                  icon={<Users2 className="h-5 w-5" />}
                  title={t(
                    'library.folder.permissions.teamAccess',
                    'Team Access',
                  )}
                  description={t(
                    'library.folder.permissions.teamAccessDescription',
                    'Anyone in your team can view this folder',
                  )}
                />
                <AccessTypeCard
                  isSelected={!isPublic}
                  onClick={() => handleAccessTypeChange(false)}
                  icon={<Lock className="h-5 w-5" />}
                  title={t(
                    'library.folder.permissions.restrictedAccess',
                    'Restricted Access',
                  )}
                  description={t(
                    'library.folder.permissions.restrictedAccessDescription',
                    'Only selected people can access this folder',
                  )}
                />
              </div>
            </>
          )}

          {/* Members Section */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-medium">
                {isPublic
                  ? t(
                      'library.folder.permissions.allTeamMembers',
                      'All Team Members',
                    )
                  : t(
                      'library.folder.permissions.peopleWithAccess',
                      'People with Access',
                    )}
              </h3>
              <Collapsible open={!isPublic && canManagePermissions}>
                <CollapsibleContent>
                  <span className="text-sm text-muted-foreground">
                    {selectedUsers.length}{' '}
                    {selectedUsers.length === 1 ? 'person' : 'people'}
                  </span>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Search Input */}
            <Collapsible open={!isPublic || !canManagePermissions}>
              <CollapsibleContent>
                <div className="relative mb-4 px-0.5 py-0.5">
                  <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
                  <Input
                    placeholder={t(
                      'library.folder.permissions.searchPeople',
                      'Search people',
                    )}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Members Table */}
            <div className="rounded-md border mx-0.5">
              <ScrollArea
                className={cn(
                  filteredMembers.length <= 5 ? 'h-auto' : 'h-[300px]',
                )}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      {canManagePermissions && (
                        <TableHead className="w-[50px]">
                          <div
                            className={cn(
                              'transition-opacity duration-300',
                              !isPublic ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        </TableHead>
                      )}
                      <TableHead className="w-[35%]">
                        {t('common.name', 'Name')}
                      </TableHead>
                      <TableHead className="w-[35%]">
                        {t('common.email', 'Email')}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('library.folder.permissions.status', 'Status')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={canManagePermissions ? 4 : 3}
                          className="h-24 text-center text-muted-foreground"
                        >
                          {t(
                            'library.folder.permissions.noMatchingMembers',
                            'No matching team members found',
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredMembers.map((member) => {
                        const isOwner = member._id === folder.ownerId;
                        const hasAccess =
                          isPublic || selectedUsers.includes(member._id);

                        return (
                          <TableRow key={member._id}>
                            {canManagePermissions && (
                              <TableCell className="w-[50px]">
                                <div
                                  className={cn(
                                    'transition-all duration-300 transform',
                                    !isPublic
                                      ? 'opacity-100 scale-100'
                                      : 'opacity-0 scale-75 pointer-events-none',
                                  )}
                                >
                                  <Checkbox
                                    checked={hasAccess}
                                    disabled={isOwner || isPublic}
                                    onCheckedChange={(checked) =>
                                      handleToggleUser(
                                        member._id,
                                        checked as boolean,
                                      )
                                    }
                                  />
                                </div>
                              </TableCell>
                            )}
                            <TableCell className="font-medium">
                              {member.name || '—'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {member.email}
                            </TableCell>
                            <TableCell className="text-right">
                              <MemberStatusBadge
                                userId={member._id}
                                folderId={folder.ownerId}
                                teamOwnerId={selectedTeam?.ownerId}
                                isOwner={isOwner}
                                hasAccess={hasAccess}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </div>
        </div>

        <DialogFooter>
          {canManagePermissions ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
                {isSaving
                  ? t('library.folder.permissions.saving', 'Saving...')
                  : t('library.folder.permissions.saveChanges', 'Save changes')}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose}>
              {t('common.close', 'Close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
